
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TopicCard } from "./TopicCard";

interface TopicsProps {
  onSelectTopic: (topicId: string) => void;
}

/**
 * Topics component - displays all available topics with progress tracking
 * Calculates and shows completion progress for each topic based on lessons completed
 */
export const Topics = ({ onSelectTopic }: TopicsProps) => {
  // Fetch all topics
  const { data: topics = [], isLoading: isLoadingTopics } = useQuery({
    queryKey: ['topics'],
    queryFn: async () => {
      console.log("📚 Fetching topics");
      const { data, error } = await supabase
        .from('topics')
        .select('*')
        .eq('status', 'active')
        .order('order_index');
      
      if (error) {
        console.error("❌ Error fetching topics:", error);
        throw error;
      }

      console.log("✅ Topics fetched:", data?.length || 0);
      return data;
    },
  });

  // Fetch user progress for all lessons
  const { data: userProgress = [], isLoading: isLoadingProgress } = useQuery({
    queryKey: ['userProgressAll'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log("👤 No user session found for progress tracking");
        return [];
      }

      console.log("📈 Fetching all user progress");
      const { data, error } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', session.user.id);

      if (error) {
        console.error("❌ Error fetching user progress:", error);
        throw error;
      }

      console.log("📊 User progress data:", data?.length || 0, "records");
      return data;
    },
  });

  // Fetch topic statistics (modules and lessons count per topic)
  const { data: topicStats = [], isLoading: isLoadingStats } = useQuery({
    queryKey: ['topicStatistics'],
    queryFn: async () => {
      console.log("📊 Fetching topic statistics");
      
      // Get modules with their topic relationships
      const { data: modules, error: modulesError } = await supabase
        .from('modules')
        .select('id, topic_id')
        .eq('status', 'active');

      if (modulesError) {
        console.error("❌ Error fetching modules:", modulesError);
        throw modulesError;
      }

      // Get lessons with their module relationships
      const { data: lessons, error: lessonsError } = await supabase
        .from('lessons')
        .select('id, module_id')
        .eq('status', 'active');

      if (lessonsError) {
        console.error("❌ Error fetching lessons:", lessonsError);
        throw lessonsError;
      }

      // Calculate statistics per topic
      const topicStatsMap = new Map();
      
      // Initialize stats for each topic
      topics.forEach(topic => {
        topicStatsMap.set(topic.id, {
          topicId: topic.id,
          totalLessons: 0,
          lessonIds: []
        });
      });

      // Count lessons per topic through modules
      lessons?.forEach(lesson => {
        const module = modules?.find(m => m.id === lesson.module_id);
        if (module?.topic_id) {
          const stats = topicStatsMap.get(module.topic_id);
          if (stats) {
            stats.totalLessons++;
            stats.lessonIds.push(lesson.id);
          }
        }
      });

      const result = Array.from(topicStatsMap.values());
      console.log("📊 Topic statistics calculated:", result);
      return result;
    },
    enabled: topics.length > 0, // Only run after topics are loaded
  });

  // Calculate progress for each topic
  const getTopicProgress = (topicId: string) => {
    const stats = topicStats.find(s => s.topicId === topicId);
    if (!stats || stats.totalLessons === 0) {
      return {
        totalLessons: 0,
        completedLessons: 0,
        progressPercentage: 0
      };
    }

    // Count completed lessons for this topic
    const completedLessons = stats.lessonIds.filter(lessonId =>
      userProgress.some(progress => 
        progress.lesson_id === lessonId && progress.status === 'completed'
      )
    ).length;

    const progressPercentage = Math.round((completedLessons / stats.totalLessons) * 100);

    console.log("📈 Topic progress calculated:", {
      topicId,
      totalLessons: stats.totalLessons,
      completedLessons,
      progressPercentage
    });

    return {
      totalLessons: stats.totalLessons,
      completedLessons,
      progressPercentage
    };
  };

  if (isLoadingTopics || isLoadingProgress || isLoadingStats) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div 
            key={i}
            className="h-56 bg-gray-100 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {topics.map((topic) => (
        <TopicCard
          key={topic.id}
          topic={topic}
          progress={getTopicProgress(topic.id)}
          onClick={() => {
            console.log("🎯 Selected topic:", { id: topic.id, title: topic.title, order_index: topic.order_index });
            onSelectTopic(topic.id);
          }}
        />
      ))}
    </div>
  );
};
